import moment from "moment-timezone";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fitsBalance, fitsMaxTaken, getHoursTaken, getMaxRequestedHours, getMinBalance, isInProbation } from "../../../../../../../utils/helpers/timeOffRules";

export const usePayCodeValidations = ({ user, timeOffPlans } = {}) => {
  const { t } = useTranslation();

  const getTimeOffPlan = useCallback((timeSheet, timeOffPlans) => {
    return timeOffPlans?.find((plan) => {
      return (
        plan.startDate <= timeSheet.date
        && plan.endDate >= timeSheet.date
        && plan.plan.payCode === timeSheet.payCode?.id
        // the probation pay code should also be included here
      );
    });
  }, []);

  const isRequired = useCallback((value, timeSheet, allTimeSheets, originalAllTimeSheets) => {
    if (!value) {
      return t("pay-code-required");
    }
    return null;
  }, [t, timeOffPlans]);

  const isTimeOffPlanRequired = useCallback((value, timeSheet) => {
    if (!value?.requireTimeOffPlan) {
      return null;
    }
    const userTimeOffPlan = getTimeOffPlan(timeSheet, timeOffPlans);
    if (!userTimeOffPlan) {
      return t('associated-time-off-plan-required');
    }
    return null;
  }, [t, timeOffPlans]);

  const isOnTimeOffProbation = useCallback((value, timeSheet, allTimeSheets, originalAllTimeSheets) => {
    const userTimeOffPlan = getTimeOffPlan(timeSheet, timeOffPlans);
    if (!userTimeOffPlan) {
      return null;
    }
    const plan = userTimeOffPlan.plan;

    // probation as the backend reads it: value >= days since the hire (or seniority) date,
    // no date = not in probation; with allowRequests the backend allows it (or moves it to
    // the plan's probation pay code)
    if (!plan.prohibition || plan.prohibition.allowRequests) {
      return null;
    }
    if (isInProbation(plan, user, timeSheet.date)) {
      return t('user-on-probation');
    }

    return null;
  }, [t, timeOffPlans, user]);

  const hasSurpassedAvailableTimeOffBalance = useCallback((value, timeSheet, allTimeSheets, originalAllTimeSheets) => {
    const userTimeOffPlan = getTimeOffPlan(timeSheet, timeOffPlans);
    if (!userTimeOffPlan) {
      return null;
    }

    // only the time sheets on this plan (its pay code, within its dates) count against its balance
    const onThisPlan = (item) => item && getTimeOffPlan(item, timeOffPlans)?.id === userTimeOffPlan.id;
    let diff = 0;
    allTimeSheets.forEach((item) => {
      const existing = originalAllTimeSheets?.find((ts) => ts.id === item.id);
      const itemDuration = onThisPlan(item) ? moment.duration(item.duration, 'seconds').asHours() : 0;
      const existingDuration = onThisPlan(existing) ? moment.duration(existing.duration, 'seconds').asHours() : 0;
      diff += itemDuration - existingDuration;
    });
    if (diff > 0 && !fitsBalance(userTimeOffPlan.plan, userTimeOffPlan.bucket?.available, diff)) {
      // what the change may still add: the balance down to the plan's minimum balance (the edited
      // rows' stored hours are already in it)
      const room = (Number(userTimeOffPlan.bucket?.available) || 0) - getMinBalance(userTimeOffPlan.plan);
      return t('available-hours-exceeded', { available: Math.max(Number(room.toFixed(2)), 0) });
    }

    return null;
  }, [t, timeOffPlans]);

  // "Maximum hours allowed to be taken" is a limit on the plan year (clock-in TimeOffTakenService):
  // what the plan already took, the edited time sheets at their stored values, plus the net hours
  // this edit adds to the plan. The employee's tier on the time sheet's date
  const hasSurpassedMaxHoursTaken = useCallback((value, timeSheet, allTimeSheets, originalAllTimeSheets) => {
    const userTimeOffPlan = getTimeOffPlan(timeSheet, timeOffPlans);
    if (!userTimeOffPlan) {
      return null;
    }
    const maxRequestedHours = getMaxRequestedHours(userTimeOffPlan.plan, user, timeSheet.date);
    if (maxRequestedHours === undefined) {
      return null;
    }
    const onThisPlan = (item) => item && getTimeOffPlan(item, timeOffPlans)?.id === userTimeOffPlan.id;
    let diff = 0;
    (allTimeSheets || []).forEach((item) => {
      const existing = originalAllTimeSheets?.find((ts) => ts.id === item.id);
      const itemDuration = onThisPlan(item) ? moment.duration(item.duration, 'seconds').asHours() : 0;
      const existingDuration = onThisPlan(existing) ? moment.duration(existing.duration, 'seconds').asHours() : 0;
      diff += itemDuration - existingDuration;
    });
    if (diff <= 0) {
      return null;
    }
    const taken = getHoursTaken(userTimeOffPlan, timeOffPlans);
    if (!fitsMaxTaken(maxRequestedHours, taken, diff)) {
      return t('max-hours-taken-exceeded', { taken, max: maxRequestedHours });
    }

    return null;
  }, [t, timeOffPlans, user]);

  return useMemo(() => {
    return [
      isRequired,
      isTimeOffPlanRequired,
      isOnTimeOffProbation,
      hasSurpassedAvailableTimeOffBalance,
      hasSurpassedMaxHoursTaken,
    ];
  }, [isRequired, isTimeOffPlanRequired, isOnTimeOffProbation, hasSurpassedAvailableTimeOffBalance, hasSurpassedMaxHoursTaken]);
}
